import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ShoppingCart, Lock, Star, Sparkles, Crown } from 'lucide-react';
import { CONSTANTS } from '../data.js';
import { useGameDataContext } from '../app/GameProviders';

interface Props {
  show: boolean;
  onClose: () => void;
  money: number;
  currentDay: number;
  premiumCookbookUnlocked: boolean;
  onPurchaseItem: (itemId: string) => void;
  onUnlockPremiumCookbook: () => void;
  ownedItems: Set<string>;
}

interface ShopCategory {
  name: string;
  icon: React.ReactNode;
  color: string;
  items: string[];
}

export function Shop({ 
  show, 
  onClose, 
  money, 
  currentDay, 
  premiumCookbookUnlocked, 
  onPurchaseItem, 
  onUnlockPremiumCookbook,
  ownedItems 
}: Props) {
  const [selectedCategory, setSelectedCategory] = useState<string>('premium_basic');
  const { MATERIALS, SHOP_ITEMS } = useGameDataContext();

  const categories: Record<string, ShopCategory> = {
    premium_basic: {
      name: 'Premium Basics',
      icon: <Star className="w-5 h-5" />,
      color: '#10b981',
      items: Object.entries(SHOP_ITEMS)
        .filter(([_, item]) => item.category === 'premium_basic')
        .map(([id]) => id)
    },
    exotic: {
      name: 'Exotic Ingredients',
      icon: <Sparkles className="w-5 h-5" />,
      color: '#7c3aed',
      items: Object.entries(SHOP_ITEMS)
        .filter(([_, item]) => item.category === 'exotic')
        .map(([id]) => id)
    },
    magical: {
      name: 'Magical Ingredients',
      icon: <Crown className="w-5 h-5" />,
      color: '#be185d',
      items: Object.entries(SHOP_ITEMS)
        .filter(([_, item]) => item.category === 'magical')
        .map(([id]) => id)
    }
  };

  const canAffordPremiumCookbook = money >= CONSTANTS.SHOP.PREMIUM_COOKBOOK_COST;
  const canUnlockPremiumCookbook = currentDay >= CONSTANTS.SHOP.PREMIUM_COOKBOOK_UNLOCK_DAY;

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/95 z-[2000] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-5xl h-[80vh] flex flex-col border border-slate-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700 bg-slate-800/90">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-6 h-6 text-emerald-400" />
              <h2 className="text-2xl font-bold">Ingredient Shop</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-300">
                <span className="text-amber-400 font-semibold">${money}</span> available
              </div>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-white p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="w-64 border-r border-slate-700 p-4 flex flex-col gap-4 bg-slate-800/50">
              {/* Premium Cookbook Unlock */}
              <div className="mb-4">
                <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-purple-400" />
                    <span className="font-semibold text-purple-300">Premium Cookbook</span>
                  </div>
                  <p className="text-xs text-slate-300 mb-3">
                    Unlock advanced recipes with magical ingredients!
                  </p>
                  {premiumCookbookUnlocked ? (
                    <div className="text-xs text-green-400 font-semibold">
                      ✓ Unlocked!
                    </div>
                  ) : (
                    <div>
                      <div className="text-xs text-slate-400 mb-2">
                        Cost: ${CONSTANTS.SHOP.PREMIUM_COOKBOOK_COST}
                      </div>
                      {!canUnlockPremiumCookbook ? (
                        <div className="text-xs text-orange-400 mb-2">
                          <Lock className="w-3 h-3 inline mr-1" />
                          Available on Day {CONSTANTS.SHOP.PREMIUM_COOKBOOK_UNLOCK_DAY}
                        </div>
                      ) : (
                        <button
                          onClick={onUnlockPremiumCookbook}
                          disabled={!canAffordPremiumCookbook}
                          className={`w-full text-xs py-2 px-3 rounded-md font-semibold transition-colors ${
                            canAffordPremiumCookbook
                              ? 'bg-purple-600 hover:bg-purple-500 text-white'
                              : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                          }`}
                        >
                          {canAffordPremiumCookbook ? 'Unlock Now' : 'Not Enough Money'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div className="flex flex-col gap-1">
                {Object.entries(categories).map(([key, category]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={`flex items-center gap-3 p-3 rounded-md text-left transition-colors ${
                      selectedCategory === key
                        ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-600/30'
                        : 'hover:bg-slate-700/50 text-slate-300'
                    }`}
                    style={{ 
                      borderColor: selectedCategory === key ? category.color + '50' : undefined,
                      backgroundColor: selectedCategory === key ? category.color + '20' : undefined 
                    }}
                  >
                    <div style={{ color: category.color }}>
                      {category.icon}
                    </div>
                    <div>
                      <div className="font-medium">{category.name}</div>
                      <div className="text-xs text-slate-400">
                        {category.items.length} items
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories[selectedCategory]?.items.map(itemId => {
                  const shopItem = SHOP_ITEMS[itemId];
                  const material = MATERIALS[itemId];
                  const isOwned = ownedItems.has(itemId);
                  const canAfford = money >= shopItem.cost;
                  
                  if (!material || !shopItem) return null;

                  return (
                    <motion.div
                      key={itemId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-lg border transition-all ${
                        isOwned
                          ? 'bg-green-500/10 border-green-500/30'
                          : 'bg-slate-700/30 border-slate-600/30 hover:border-slate-500/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="text-3xl">{material.icon}</div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-white">{material.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-lg font-bold text-amber-400">
                              ${shopItem.cost}
                            </span>
                            {isOwned && (
                              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                                Owned
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="text-xs text-slate-400 mb-3">
                        {shopItem.category === 'premium_basic' && 'Premium cooking essential'}
                        {shopItem.category === 'exotic' && 'Rare delicacy ingredient'}
                        {shopItem.category === 'magical' && 'Mystical cooking component'}
                      </div>

                      <button
                        onClick={() => onPurchaseItem(itemId)}
                        disabled={isOwned || !canAfford}
                        className={`w-full py-2 px-3 rounded-md font-semibold text-sm transition-colors ${
                          isOwned
                            ? 'bg-green-500/20 text-green-400 cursor-default'
                            : canAfford
                            ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                            : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                        }`}
                      >
                        {isOwned ? 'Purchased' : canAfford ? 'Buy Now' : 'Not Enough Money'}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
